import { Divider } from 'antd'
import React, { type ReactElement } from 'react'
import styled from 'styled-components'
import { theme } from '../../config/theme'
import { Heading, MoreInfoLink, Text } from '../../general'

type FormCardProps = {
  title?: string | React.ReactNode
  subtitle?: string
  helpArticleUrl?: string
  children?: React.ReactNode
  rightChildren?: React.ReactNode
  leftChildren?: React.ReactNode
  level?: 1 | 2
  className?: string
  padding?: string
  contentGap?: number
  id?: string
  testId?: string
  dataAttributes?: Record<`data-${string}`, string>
}

const FormCard = ({
  title,
  subtitle,
  helpArticleUrl,
  children,
  rightChildren,
  level = 1,
  className,
  leftChildren,
  padding,
  contentGap,
  id,
  testId,
  dataAttributes
}: FormCardProps): ReactElement => {
  const backgroundColor =
    level === 1 ? theme.color.neutral.color11 : theme.color.neutral.color10

  const hasChildren = React.Children.count(children) > 0

  const isStringTitle = typeof title === 'string'

  return (
    <CardContainer
      $backgroundColor={backgroundColor}
      className={className}
      id={id || undefined}
      data-testid={testId}
      {...dataAttributes}
    >
      <HeaderRowContainer>
        {leftChildren}
        <HeaderMainContainer>
          {title && isStringTitle && (
            <HeaderContainer>
              <TitleContainer>
                {level === 1 && (
                  <FormCardHeading level={3}>{title}</FormCardHeading>
                )}
                {level === 2 && (
                  <FormCardHeading level={4}>{title}</FormCardHeading>
                )}
                {helpArticleUrl && (
                  <MoreInfoLink helpArticleUrl={helpArticleUrl} />
                )}
              </TitleContainer>
              {rightChildren}
            </HeaderContainer>
          )}
          {title && !isStringTitle && (
            <HeaderContainer>
              {title}
              {rightChildren}
            </HeaderContainer>
          )}
          {title && subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderMainContainer>
      </HeaderRowContainer>
      {title && hasChildren && <StyledDivider />}
      {hasChildren && <FormChildren $gap={contentGap}>{children}</FormChildren>}
    </CardContainer>
  )
}

const CardContainer = styled.div<{
  $backgroundColor: string
  $padding?: string
}>`
  padding: ${(props) => props?.$padding || '24px'};
  border-radius: 20px;
  background-color: ${(props) => props?.$backgroundColor};
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const FormChildren = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 24}px;
`

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const HeaderMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const HeaderRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
  margin: 16px 0 24px 0;
`

export default FormCard
